import type { ProductVariant } from '../types';
import gsap from 'gsap';

export default class PdpPurchase extends HTMLElement {
  private resizeObserver: ResizeObserver | null = null;
  private section: HTMLElement | null = null;
  private sizeLabel: HTMLElement | null = null;
  private defaultSizeLabel = '';
  private nextSizeLabel = '';
  private sizeLabelTimeline: gsap.core.Timeline | null = null;
  private primaryButton: HTMLButtonElement | null = null;

  // CSS handles scrolling through two sticky tracks bounded by the product section.
  // Only their starting positions need DOM measurements; button dimensions live in CSS.
  private updatePosition = (): void => {
    const top = this.getBoundingClientRect().top;
    const anchor = this.querySelector<HTMLElement>('[data-pdp-size-anchor]');
    // Document offset lets the CTA track start at the top of the page.
    this.style.setProperty('--pdp-section-top', `${top + window.scrollY}px`);
    if (anchor) {
      // Section-relative offset keeps the selector in the summary until it reaches the CTA.
      this.style.setProperty('--pdp-size-top', `${anchor.getBoundingClientRect().top - top}px`);
    }
  };

  private onVariantChange = (event: Event): void => {
    const variant = (event as CustomEvent<ProductVariant | null>).detail;
    // Draft changes stay inside the modal. Only dismissal publishes a selection;
    // a successful add publishes null to restore the original translated label.
    this.updateSizeLabel(
      variant
        ? [variant.base, variant.length].filter(Boolean).join(' / ') || this.defaultSizeLabel
        : this.defaultSizeLabel,
    );

    // An available selection turns the primary CTA into a direct add, not a dialog trigger.
    if (variant?.available) {
      this.primaryButton?.removeAttribute('aria-haspopup');
      this.primaryButton?.removeAttribute('aria-controls');
    } else {
      this.primaryButton?.setAttribute('aria-haspopup', 'dialog');
      this.primaryButton?.setAttribute('aria-controls', 'size-selector-modal-dialog');
    }
  };

  private updateSizeLabel(text: string): void {
    const label = this.sizeLabel;
    if (!label || text === this.nextSizeLabel) return;

    this.nextSizeLabel = text;
    this.sizeLabelTimeline?.kill();
    // Keep the button's accessible name available while its visible label fades out.
    label.closest('button')?.setAttribute('aria-label', text);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      label.textContent = text;
      gsap.set(label, { autoAlpha: 1 });
      return;
    }

    // Match modular buttons: fade out, swap the label, then fade in after a short pause.
    this.sizeLabelTimeline = gsap.timeline();
    this.sizeLabelTimeline.to(label, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
    this.sizeLabelTimeline.call(
      () => {
        label.textContent = text;
      },
      [],
      0.2,
    );
    this.sizeLabelTimeline.to(label, { autoAlpha: 1, duration: 0.2, ease: 'power2.out' }, 0.3);
  }

  connectedCallback(): void {
    if (!this.hasAttribute('data-sticky-size')) return;

    this.section = this.closest('.main-product');
    this.sizeLabel = this.querySelector('[data-pdp-size-label]');
    this.defaultSizeLabel = this.sizeLabel?.textContent?.trim() ?? '';
    this.nextSizeLabel = this.defaultSizeLabel;
    this.primaryButton = this.querySelector('[data-add-selected-variant] button');
    this.section?.addEventListener('size-selector:change', this.onVariantChange);

    this.updatePosition();
    // Recalculate after layout or viewport changes, without running JavaScript on scroll.
    this.resizeObserver = new ResizeObserver(this.updatePosition);
    this.resizeObserver.observe(document.body);
    window.addEventListener('resize', this.updatePosition);
  }

  disconnectedCallback(): void {
    this.sizeLabelTimeline?.kill();
    if (this.sizeLabel) {
      this.sizeLabel.textContent = this.nextSizeLabel;
      gsap.set(this.sizeLabel, { clearProps: 'opacity,visibility' });
    }
    this.section?.removeEventListener('size-selector:change', this.onVariantChange);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.updatePosition);
  }
}

customElements.define('pdp-purchase', PdpPurchase);
